import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { DataTable } from '@/components/data/DataTable'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { AutoRefreshControl, useRefreshInterval } from '@/components/ui/auto-refresh-control'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useIngressActive, useIngressHistory, useIngressSummary, ingressKeys } from '../hooks'
import { createActiveColumns, createHistoryColumns } from '../columns'
import type { IngressPeriod, IngressActiveItem, IngressHistoryItem, IngressSummaryItem } from '../api'

export function IngressMonitoringPage() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [historyPeriod, setHistoryPeriod] = useState<IngressPeriod>('day')
    /**
     * The shared control, not this page's own copy of one.
     *
     * The version it replaces offered 10s/30s/1m and no 5m, hard-coded its English ("Auto
     * Refresh", "Off", "Every …") so it never translated, and forgot the choice on every
     * navigation. Two implementations of the same control is two places to fix anything, and the
     * one nobody remembers is the one that falls behind.
     */
    const [autoRefresh, setAutoRefresh] = useRefreshInterval('ingress-monitoring.refresh', 10_000)
    const [isRefreshing, setIsRefreshing] = useState(false)

    const { data: activeData, isLoading: activeLoading, dataUpdatedAt } = useIngressActive(autoRefresh)
    const { data: historyData, isLoading: historyLoading } = useIngressHistory(historyPeriod)
    const { data: summaryData } = useIngressSummary(historyPeriod)

    const activeList: IngressActiveItem[] = Array.isArray(activeData) ? activeData : []
    const historyList: IngressHistoryItem[] = Array.isArray(historyData) ? historyData : []
    const summaryList: IngressSummaryItem[] = Array.isArray(summaryData) ? summaryData : []

    const activeColumns = createActiveColumns(t)
    const historyColumns = createHistoryColumns(t)

    // Compute summary totals
    const totalObjects = summaryList.reduce((sum, s) => sum + (s.noObjects || 0), 0)
    const totalVersions = summaryList.reduce((sum, s) => sum + (s.noVersions || 0), 0)
    const totalFinished = summaryList.reduce((sum, s) => sum + (s.noFinished || 0), 0)
    const totalSizeMB = summaryList.reduce((sum, s) => sum + (s.sizeMB || 0), 0)

    const periodLabels: Record<IngressPeriod, string> = {
        day: t('ingressMonitoring.lastDay'),
        week: t('ingressMonitoring.lastWeek'),
        month: t('ingressMonitoring.lastMonth'),
    }

    // Manual refresh — invalidate all ingress queries
    async function handleRefresh() {
        setIsRefreshing(true)
        await queryClient.invalidateQueries({ queryKey: ingressKeys.all })
        setTimeout(() => setIsRefreshing(false), 600)
    }

    return (
        <div className="space-y-6">
            {/* Header with refresh controls */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{t('ingressMonitoring.title')}</h1>
                    <p className="text-muted-foreground">{t('ingressMonitoring.description')}</p>
                </div>
                <AutoRefreshControl
                    value={autoRefresh}
                    onChange={setAutoRefresh}
                    onRefresh={handleRefresh}
                    isRefreshing={isRefreshing}
                    updatedAt={dataUpdatedAt}
                />
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.activeNow')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{activeList.length}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.objectsIngressed')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{totalObjects}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.versionsIngressed')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{totalVersions}</div>
                        <div className="text-xs text-muted-foreground">{totalFinished} {t('ingressMonitoring.finished')}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.totalSize')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {totalSizeMB >= 1024 ? `${(totalSizeMB / 1024).toFixed(1)} GB` : `${totalSizeMB.toFixed(1)} MB`}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Active ingress table */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold">{t('ingressMonitoring.activeIngress')}</h2>
                {activeLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <div className="text-muted-foreground">{t('common.loading')}</div>
                    </div>
                ) : activeList.length === 0 ? (
                    <Card>
                        <CardContent className="flex items-center justify-center h-32">
                            <p className="text-muted-foreground">{t('ingressMonitoring.noActiveIngress')}</p>
                        </CardContent>
                    </Card>
                ) : (
                    <DataTable
                        columns={activeColumns}
                        data={activeList}
                        searchKey="objectId"
                        searchPlaceholder={t('ingressMonitoring.searchPlaceholder')}
                    />
                )}
            </div>

            {/* History section with period filter */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">{t('ingressMonitoring.completedIngress')}</h2>
                    {/* The same segmented control as the rest of the application: a filled
                        primary button reads as an action, not the period you are already looking
                        at. */}
                    <SegmentedControl
                        size="sm"
                        value={historyPeriod}
                        onValueChange={setHistoryPeriod}
                        ariaLabel={t('ingressMonitoring.periodAria', 'History period')}
                        options={(['day', 'week', 'month'] as IngressPeriod[]).map((period) => ({
                            value: period, label: periodLabels[period],
                        }))}
                    />
                </div>
                {historyLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <div className="text-muted-foreground">{t('common.loading')}</div>
                    </div>
                ) : historyList.length === 0 ? (
                    <Card>
                        <CardContent className="flex items-center justify-center h-32">
                            <p className="text-muted-foreground">{t('ingressMonitoring.noHistory')}</p>
                        </CardContent>
                    </Card>
                ) : (
                    <DataTable
                        columns={historyColumns}
                        data={historyList}
                        searchKey="objectId"
                        searchPlaceholder={t('ingressMonitoring.searchPlaceholder')}
                    />
                )}
            </div>
        </div>
    )
}
